import type { ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { PLATFORM_SPECS } from '../../../shared/platforms';
import type { PlatformId, SocialAccount } from '../../../shared/types';
import { Badge, Button, Card, PlatformIcon, Tooltip } from '../ui';
import { cn } from '../../lib/utils';

export interface ConnectionCardProps {
  platform: PlatformId;
  accounts: SocialAccount[];
  configured: boolean;
  actions?: ReactNode;
  /** Called when the small remove button on an account row is clicked. */
  onDisconnect?: (account: SocialAccount) => void;
  /** Account id currently being disconnected — shows a busy state on that row. */
  disconnectingId?: string | null;
  className?: string;
}

/** Per-platform connect card. WEB-4 owns the connect/disconnect interactions. */
export function ConnectionCard({
  platform,
  accounts,
  configured,
  actions,
  onDisconnect,
  disconnectingId,
  className,
}: ConnectionCardProps) {
  const spec = PLATFORM_SPECS[platform];
  const connected = accounts.length > 0;

  return (
    <Card className={cn('flex flex-col gap-3 px-5 py-4', className)}>
      <div className="flex items-start gap-3">
        <PlatformIcon platform={platform} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium text-fg">{spec.name}</p>
            {spec.comingSoon && <Badge tone="neutral">Coming soon</Badge>}
            {!spec.comingSoon && connected && <Badge tone="success">Connected</Badge>}
            {!spec.comingSoon && !connected && !configured && <Badge tone="info">Simulate</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted">{spec.blurb}</p>
        </div>
      </div>
      {connected && (
        <ul className="space-y-1.5">
          {accounts.map((account) => (
            <li
              key={account.id}
              className="flex items-center gap-2 rounded-md border border-border bg-elevated/40 px-2.5 py-1.5 text-sm text-muted"
            >
              <span className="min-w-0 flex-1 truncate">{account.displayName}</span>
              {account.status === 'simulated' && (
                <Badge tone="info" size="sm">
                  Simulated
                </Badge>
              )}
              {account.status === 'expired' && (
                <Tooltip content="Reconnect to refresh the token">
                  <Badge tone="warning" size="sm" dot>
                    Token expired
                  </Badge>
                </Tooltip>
              )}
              {account.status === 'error' && (
                <Badge tone="danger" size="sm" dot>
                  Error
                </Badge>
              )}
              {onDisconnect && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Disconnect ${account.displayName}`}
                  loading={disconnectingId === account.id}
                  onClick={() => onDisconnect(account)}
                >
                  <X className="size-3.5" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!spec.comingSoon && !configured && !connected && (
        <p className="flex items-start gap-1.5 text-xs text-subtle">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
          No app credentials configured for {spec.shortName} — simulate an account to explore
          publishing here.
        </p>
      )}
      {actions && <div className="flex flex-wrap items-center gap-2 pt-1">{actions}</div>}
    </Card>
  );
}
